//src/stores/CustomerStore.ts
import { defineStore } from 'pinia'
import { computed, markRaw, ref } from 'vue'
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  documentId,
  FirestoreError,
  updateDoc
} from 'firebase/firestore'
import { db } from '@/firebase'
import {
  applyMemberships,
  MemberAccountConverter,
  ownMembershipQuery,
  schoolAccountDoc
} from '@/firebase/memberships'
import { Account, AccountConverter } from '@/types/Account'
import { BillingStatus, Store, StoreConverter } from '@/types/Store'
import { ScheduledEventConverter, ScheduledEvent } from '@/types/Store/Event/Event'
import { Aircraft, AircraftConverter } from '@/types/Store/Aircraft'
import { Room, RoomConverter } from '@/types/Store/Room'
import { Module } from '@/types/Store/StoreEnums'
import { FirebaseCollections, FirestorePaths } from '@/utils/firestore/FirestorePaths'
import { Client, ClientConverter } from '@/types/Client'
import { Roles } from '@/types/Helper/Enums'
import { getStoreAccountDirectory, type StoreAccountDirectory } from '@/firebase/accountDirectory'
import { isFreeStore } from '@/utils/storeCapacity'

// A load that ended without data. Exposed so the UI can say what happened
// instead of spinning forever on `undefined`.
export type LoadFailureKind = 'not-found' | 'permission-denied' | 'error'

export interface LoadFailure {
  kind: LoadFailureKind
  id: string
  code?: string
  message: string
}

const toLoadFailure = (id: string, error: FirestoreError): LoadFailure => ({
  kind: error.code === 'permission-denied' ? 'permission-denied' : 'error',
  id,
  code: error.code,
  message: error.message
})

/**
 * An inactive member's school: the rules let only active members read a
 * school, so it isn't opened at all — HomeView sends them to reactivation.
 */
const inactiveMembership = (storeId: string): LoadFailure => ({
  kind: 'permission-denied',
  id: storeId,
  message: `Your membership at ${storeId} is inactive.`
})

const errorText = (error: unknown): string => {
  const message = (error as { message?: unknown } | null)?.message
  return typeof message === 'string' ? message : String(error)
}

/**
 * Why the account list didn't load, in words a person can act on. A bare
 * error code from the server ("internal") never reaches the screen; the
 * details go to the console.
 */
const accountsLoadMessage = (error: unknown): string => {
  console.error('[accounts] the account list failed to load', error)
  const code = String((error as { code?: unknown })?.code ?? '')
  if (code.endsWith('permission-denied')) return "You don't have access to this school's accounts."
  if (code.endsWith('unavailable') || code.endsWith('deadline-exceeded'))
    return "The account list couldn't be reached. Check your connection, then press Refresh."
  const message = errorText(error)
  return /^[a-z-]+$/i.test(message.trim())
    ? "The account list couldn't be loaded. Press Refresh to try again; if it keeps happening, contact support."
    : message
}

export const UseCustomerStore = defineStore('customer', () => {
  const currentStore = ref<Store>()
  const currentUser = ref<Account>()
  const billingChangeInProgress = ref(false)
  const settingsVersion = ref(0)
  const selectedStore = computed(() => currentStore.value)
  const requestedStoreCode = ref('')
  const selectedStoreCode = computed(
    () => requestedStoreCode.value || currentStore.value?.storeCode || ''
  )

  const accountLoadError = ref<LoadFailure | null>(null)
  const storeLoadError = ref<LoadFailure | null>(null)

  const customers = ref<Map<string, Account>>(new Map())
  const clients = ref<Map<string, Client>>(new Map())
  const clientsList = computed(() => Array.from(clients.value.values()))

  const pilots = computed(() =>
    Array.from(customers.value.values()).filter((cust) => cust.pilot != null)
  )
  const instructors = computed(() =>
    pilots.value.filter(
      (pilot) =>
        pilot.hasRoles(
          [Roles.Instructor, Roles.HeadInstructor, Roles.CFI, Roles.CFII, Roles.MEI],
          selectedStoreCode.value
        ) ||
        pilot.pilot!.groundInsRatings.length > 0 ||
        pilot.pilot!.ratings.some((r) => r && r.instructorRatings.length > 0)
    )
  )
  const customersList = computed(() => Array.from(customers.value.values()))
  const accountDirectory = ref<StoreAccountDirectory | null>(null)
  const totalAccountCount = computed(() => accountDirectory.value?.total)
  const schoolStudentCount = computed(() => accountDirectory.value?.schoolStudentCount)
  const includeInactiveAccounts = ref(false)
  const accountsLoadError = ref<string | null>(null)
  const accountsLoading = computed(() => activeLoading.value.has('Loading Store Accounts'))

  const aircraft = ref<Map<string, Aircraft>>(new Map())
  const aircraftList = computed(() => Array.from(aircraft.value.values()))

  const events = ref<ScheduledEvent[]>([])
  const eventWindow = ref<{ start: Date; end: Date } | null>(null)
  const rooms = ref<Room[]>([])

  // ── Listener bookkeeping ──────────────────────────────────────────────────
  //
  // The account listener and everything tied to the current store are kept
  // apart, so switching or reloading a store tears down ALL of that store's
  // listeners (store doc, accounts, aircraft, rooms, clients, events) and
  // never touches the signed-in account.

  let accountUnsubscribe: (() => void) | null = null
  let activeAccountId = ''
  let membershipUnsubscribe: (() => void) | null = null
  let ownMemberships: import('firebase/firestore').DocumentData[] | null = null
  let ownProfile: Account | null = null

  let storeDocUnsubscribe: (() => void) | null = null
  let storeUnsubscribes: (() => void)[] = []
  let eventUnsubscribe: (() => void) | null = null
  let activeStoreId = ''
  // Child listeners start once per store load, not on every store-doc update.
  let childListenersStoreId = ''
  let accountDirectoryUnsubscribe: (() => void) | null = null
  const accountProfileListeners = new Map<string, () => void>()
  const accountProfileBatches = new Map<string, Map<string, Account>>()
  const referencedAccounts = new Map<string, Account>()
  const referencedAccountListeners = new Map<string, () => void>()
  const pendingAccountBatches = new Set<string>()
  let selectedAccountIds = new Set<string>()
  let accountRequestVersion = 0
  let accountSubscriptionEpoch = 0

  const activeLoading = ref<Set<string>>(new Set())

  const isAnythingLoading = computed(() => {
    return activeLoading.value.size > 0
  })

  const isBlockingLoad = computed(
    () => activeLoading.value.has('Loading Account') || activeLoading.value.has('Loading Store')
  )

  const isStoreBillingCurrent = computed(() => {
    const store = currentStore.value
    if (!store) return false

    if (isFreeStore(store)) return true
    if (typeof store.billing?.isCurrent === 'boolean') return store.billing.isCurrent

    return (
      store.billingStatus === BillingStatus.Active || store.billingStatus === BillingStatus.Trial
    )
  })

  const VALIDATION = {
    EMAIL_REGEX: /^[a-zA-Z0-9](\.?[a-zA-Z0-9]+)*@[^\s@]+\.[^\s@]+$/,
    PHONE_REGEX: /^\+?(\d{1,3})?[\s.-]?(\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}$/,
    FILTER_TYPES: ['Active', 'Inactive'] as const
  } as const

  const startLoading = (key: string): void => {
    const next = new Set(activeLoading.value)
    next.add(key)
    activeLoading.value = next
  }

  const stopLoading = (key: string): void => {
    const next = new Set(activeLoading.value)
    next.delete(key)
    activeLoading.value = next
  }

  const clearAllLoading = (): void => {
    activeLoading.value = new Set()
  }

  const clearEventSubscription = (): void => {
    const unsubscribe = eventUnsubscribe
    if (!unsubscribe) return

    unsubscribe()
    storeUnsubscribes = storeUnsubscribes.filter((unsub) => unsub !== unsubscribe)
    eventUnsubscribe = null
    stopLoading('Loading Events')
  }

  const clearStoreScopedData = (): void => {
    storeUnsubscribes.forEach((unsub) => unsub())
    storeUnsubscribes = []
    storeDocUnsubscribe = null
    eventUnsubscribe = null
    activeStoreId = ''
    childListenersStoreId = ''
    accountRequestVersion++
    accountSubscriptionEpoch++
    accountDirectoryUnsubscribe?.()
    accountDirectoryUnsubscribe = null
    for (const unsubscribe of accountProfileListeners.values()) unsubscribe()
    accountProfileListeners.clear()
    accountProfileBatches.clear()
    for (const unsubscribe of referencedAccountListeners.values()) unsubscribe()
    referencedAccountListeners.clear()
    referencedAccounts.clear()
    pendingAccountBatches.clear()
    selectedAccountIds = new Set()
    accountDirectory.value = null
    includeInactiveAccounts.value = false
    accountsLoadError.value = null

    stopLoading('Loading Store')
    stopLoading('Loading Store Accounts')
    stopLoading('Loading Events')

    storeLoadError.value = null
    currentStore.value = undefined
    customers.value = new Map()
    clients.value = new Map()
    aircraft.value = new Map()
    events.value = []
    eventWindow.value = null
    rooms.value = []
  }

  const clearAccountSubscription = (): void => {
    accountUnsubscribe?.()
    membershipUnsubscribe?.()
    membershipUnsubscribe = null
    ownMemberships = null
    ownProfile = null
    accountUnsubscribe = null
    activeAccountId = ''
    stopLoading('Loading Account')
  }

  const userLogout = (): void => {
    clearStoreScopedData()
    clearAccountSubscription()
    clearAllLoading()
    currentUser.value = undefined
    requestedStoreCode.value = ''
    accountLoadError.value = null
  }

  // ── Account ───────────────────────────────────────────────────────────────

  const fetchAccount = (docId: string, force = false): void => {
    // Auth state can fire more than once; don't stack duplicate listeners.
    if (!force && activeAccountId === docId && accountUnsubscribe) return

    clearAccountSubscription()
    activeAccountId = docId
    accountLoadError.value = null
    startLoading('Loading Account')

    const accountDocPath = FirestorePaths.rootDocument(FirebaseCollections.Accounts, docId)
    const accountDocRef = doc(db, accountDocPath).withConverter(AccountConverter)

    const publishAccount = () => {
      if (!ownProfile || ownMemberships === null) return
      currentUser.value = markRaw(applyMemberships(ownProfile.createCopy(), ownMemberships))
      currentUser.value.contextSchoolId = requestedStoreCode.value
      stopLoading('Loading Account')
      const code = requestedStoreCode.value
      if (code && !currentUser.value.isDev() && !currentUser.value.getActive(code)) {
        clearStoreScopedData()
        storeLoadError.value = inactiveMembership(code)
      }
    }
    accountUnsubscribe = onSnapshot(
      accountDocRef,
      (docSnapshot) => {
        if (!docSnapshot.exists()) {
          console.warn(`No account found for doc ID: ${docId}`)
          ownProfile = null
          currentUser.value = undefined
          accountLoadError.value = {
            kind: 'not-found',
            id: docId,
            message: `No account document exists for ${docId}.`
          }
          stopLoading('Loading Account')
          return
        }

        try {
          const nextProfile = docSnapshot.data()
          const changedContext =
            ownProfile && nextProfile._selectedSchoolId !== ownProfile._selectedSchoolId
          ownProfile = nextProfile
          publishAccount()
          if (ownMemberships !== null) accountLoadError.value = null
          const selectedElsewhere = nextProfile._selectedSchoolId
          if (
            changedContext &&
            requestedStoreCode.value &&
            selectedElsewhere &&
            selectedElsewhere !== requestedStoreCode.value &&
            (currentUser.value?.isDev() || currentUser.value?.hasStore(selectedElsewhere))
          ) {
            // Global ledger/profile rules share this explicit school selection across tabs.
            fetchStore(selectedElsewhere)
          }
        } catch (e) {
          // A converter throw here used to leave the app spinning forever.
          console.error('fetchAccount: FAILED to convert account', docId, e)
          ownProfile = null
          currentUser.value = undefined
          accountLoadError.value = {
            kind: 'error',
            id: docId,
            code: 'convert-failed',
            message: `Account data could not be read: ${errorText(e)}`
          }
        }

        if (accountLoadError.value) stopLoading('Loading Account')
      },
      (error: FirestoreError) => {
        // An errored listener is dead; clear it so a retry can re-subscribe.
        console.error(`Error fetching account ${docId}: ${error.code} ${error.message}`)
        ownProfile = null
        accountUnsubscribe = null
        activeAccountId = ''
        currentUser.value = undefined
        accountLoadError.value = toLoadFailure(docId, error)
        stopLoading('Loading Account')
      }
    )
    membershipUnsubscribe = onSnapshot(
      ownMembershipQuery(docId),
      (snapshot) => {
        ownMemberships = snapshot.docs
          .filter((member) => member.ref.parent.parent?.parent.id === 'StoresCustomers')
          .map((member) => ({ ...member.data(), schoolId: member.ref.parent.parent!.id }))
        if (ownProfile) accountLoadError.value = null
        publishAccount()
      },
      (error) => {
        ownMemberships = null
        currentUser.value = undefined
        accountLoadError.value = toLoadFailure(docId, error)
        stopLoading('Loading Account')
      }
    )
  }

  // ── Store ─────────────────────────────────────────────────────────────────

  const fetchStore = (storeId: string, force = false): void => {
    // Account snapshots re-fire HomeView's watcher; don't restart a load
    // that is already running for the same store.
    if (!force && activeStoreId === storeId && storeDocUnsubscribe) return

    clearStoreScopedData()
    activeStoreId = storeId
    requestedStoreCode.value = storeId
    if (currentUser.value) currentUser.value.contextSchoolId = storeId

    // Inactive here: don't ask — the database would refuse, and log an error
    // in the console for something that's expected. Reactivation takes over.
    const user = currentUser.value
    if (user && !user.isDev() && user.hasStore(storeId) && !user.getActive(storeId)) {
      storeLoadError.value = inactiveMembership(storeId)
      return
    }
    const contextReady = currentUser.value
      ? updateDoc(doc(db, `Accounts/${currentUser.value.getId()}`), {
          _selectedSchoolId: storeId
        }).catch((error) => {
          storeLoadError.value = { kind: 'error', id: storeId, message: errorText(error) }
          throw error
        })
      : null
    // Handle rejection even when the school listener fails before its first snapshot.
    if (contextReady) void contextReady.catch(() => {})
    startLoading('Loading Store')

    const storeDocRef = doc(
      db,
      FirestorePaths.rootDocument(FirebaseCollections.Stores, storeId)
    ).withConverter(StoreConverter)

    const unsub = onSnapshot(
      storeDocRef,
      async (docSnapshot) => {
        if (!docSnapshot.exists()) {
          console.warn(`No store found with id: ${storeId}`)
          currentStore.value = undefined
          storeLoadError.value = {
            kind: 'not-found',
            id: storeId,
            message: `No store document exists for ${storeId}.`
          }
          stopLoading('Loading Store')
          return
        }

        let store: Store
        try {
          store = docSnapshot.data()
        } catch (e) {
          console.error('fetchStore: FAILED to convert store', storeId, e)
          currentStore.value = undefined
          storeLoadError.value = {
            kind: 'error',
            id: storeId,
            code: 'convert-failed',
            message: `Store data could not be read: ${errorText(e)}`
          }
          stopLoading('Loading Store')
          return
        }

        currentStore.value = markRaw(store)
        storeLoadError.value = null

        if (contextReady) {
          try {
            await contextReady
          } catch {
            stopLoading('Loading Store')
            return
          }
          if (activeStoreId !== storeId) return
        }

        if (childListenersStoreId !== storeId) {
          childListenersStoreId = storeId
          fetchAccounts()
          fetchAircraft()
          fetchEvents()
          fetchRooms()
          fetchClients()
        }

        stopLoading('Loading Store')
      },
      (error: FirestoreError) => {
        console.error(`Error fetching store ${storeId}: ${error.code} ${error.message}`)
        // Tear down this store's child listeners too; they would be denied
        // the same way. clearStoreScopedData resets the error, so set it after.
        clearStoreScopedData()
        storeLoadError.value = toLoadFailure(storeId, error)
      }
    )

    storeDocUnsubscribe = unsub
    storeUnsubscribes.push(unsub)
  }

  const rebuildCustomers = () => {
    const next = new Map<string, Account>(referencedAccounts)
    for (const batch of accountProfileBatches.values()) {
      for (const [id, account] of batch) {
        if (selectedAccountIds.has(id)) next.set(id, account)
      }
    }
    customers.value = next
  }

  const refreshAccountDirectory = async (): Promise<void> => {
    if (!currentUser.value || !currentStore.value) return
    const storeCode = currentStore.value.storeCode
    const requestVersion = ++accountRequestVersion
    const subscriptionEpoch = accountSubscriptionEpoch
    const includeInactive = includeInactiveAccounts.value
    startLoading('Loading Store Accounts')
    accountsLoadError.value = null
    try {
      const directory = await getStoreAccountDirectory(storeCode, includeInactive)
      if (requestVersion !== accountRequestVersion || selectedStoreCode.value !== storeCode) return
      accountDirectory.value = directory
      // Only ask for inactive members when they're wanted. A batch that names
      // even one inactive member is refused outright for anyone who isn't
      // Owner/Admin (the rules let them read active members only), losing the
      // other 29 with it — the "account list failed to load" a CFI saw.
      selectedAccountIds = new Set(
        includeInactive ? [...directory.activeIds, ...directory.inactiveIds] : directory.activeIds
      )
      // Keep existing groups stable: one added membership should not re-read
      // every later group by changing its sorted boundaries.
      const remainingIds = new Set(selectedAccountIds)
      const batches = new Map<string, string[]>()
      for (const key of accountProfileListeners.keys()) {
        const retained = (JSON.parse(key) as string[]).filter((id) => remainingIds.has(id))
        if (!retained.length) continue
        batches.set(JSON.stringify(retained), retained)
        for (const id of retained) remainingIds.delete(id)
      }
      const ids = [...remainingIds].sort()
      for (let index = 0; index < ids.length; index += 30) {
        const batch = ids.slice(index, index + 30)
        batches.set(JSON.stringify(batch), batch)
      }
      for (const [key, unsubscribe] of accountProfileListeners) {
        if (batches.has(key)) continue
        unsubscribe()
        accountProfileListeners.delete(key)
        accountProfileBatches.delete(key)
        pendingAccountBatches.delete(key)
      }
      rebuildCustomers()
      const accountsRef = collection(db, `StoresCustomers/${storeCode}/Members`).withConverter(
        MemberAccountConverter
      )
      // This listener belongs to an earlier load (another school, a newer
      // request, or it was already replaced): ignore what it reports.
      const isStale = (key: string) =>
        subscriptionEpoch !== accountSubscriptionEpoch ||
        selectedStoreCode.value !== storeCode ||
        !accountProfileListeners.has(key)

      /**
       * A batch the rules refused: the rules refuse the whole batch when they
       * refuse any one member in it, which would hide the other 29. Read its
       * members one at a time instead — each one the rules allow is listed,
       * and only those they don't are left out (and named in the console).
       */
      const listenOneByOne = (key: string, ids: string[]) => {
        const loaded = new Map<string, Account>()
        const answered = new Set<string>()
        const refused: string[] = []
        const publish = () => {
          accountProfileBatches.set(key, new Map(loaded))
          rebuildCustomers()
        }
        const answer = (id: string) => {
          if (answered.has(id)) return
          answered.add(id)
          if (answered.size < ids.length) return
          if (refused.length)
            console.warn('[accounts] not readable here, left out of the list:', refused)
          pendingAccountBatches.delete(key)
          if (!pendingAccountBatches.size) stopLoading('Loading Store Accounts')
        }
        const unsubscribes = ids.map((id) =>
          onSnapshot(
            doc(accountsRef, id),
            (snapshot) => {
              if (isStale(key)) return
              loaded.delete(id)
              if (
                snapshot.exists() &&
                (includeInactiveAccounts.value || snapshot.get('isActive') === true)
              ) {
                try {
                  loaded.set(id, markRaw(snapshot.data()!))
                } catch (error) {
                  console.error('Unable to read account', id, error)
                }
              }
              publish()
              answer(id)
            },
            () => {
              if (isStale(key)) return
              loaded.delete(id)
              if (!answered.has(id)) refused.push(id)
              publish()
              answer(id)
            }
          )
        )
        accountProfileListeners.set(key, () => unsubscribes.forEach((unsubscribe) => unsubscribe()))
      }

      for (const [key, ids] of batches) {
        if (accountProfileListeners.has(key)) continue
        pendingAccountBatches.add(key)
        const unsubscribe = onSnapshot(
          query(
            accountsRef,
            where(documentId(), 'in', ids),
            ...(!includeInactiveAccounts.value ? [where('isActive', '==', true)] : [])
          ),
          (snapshot) => {
            if (isStale(key)) return
            const accounts = new Map<string, Account>()
            for (const document of snapshot.docs) {
              try {
                accounts.set(document.id, markRaw(document.data()))
              } catch (error) {
                console.error('Unable to read account', document.id, error)
              }
            }
            accountProfileBatches.set(key, accounts)
            pendingAccountBatches.delete(key)
            rebuildCustomers()
            if (!pendingAccountBatches.size) stopLoading('Loading Store Accounts')
          },
          (error) => {
            if (isStale(key)) return
            accountProfileListeners.get(key)?.()
            if ((error as FirestoreError).code === 'permission-denied') {
              listenOneByOne(key, ids)
              return
            }
            accountsLoadError.value = accountsLoadMessage(error)
            accountProfileListeners.delete(key)
            pendingAccountBatches.delete(key)
            if (!pendingAccountBatches.size) stopLoading('Loading Store Accounts')
          }
        )
        accountProfileListeners.set(key, unsubscribe)
      }
      if (!pendingAccountBatches.size) stopLoading('Loading Store Accounts')
    } catch (error) {
      if (requestVersion !== accountRequestVersion) return
      accountsLoadError.value = accountsLoadMessage(error)
      stopLoading('Loading Store Accounts')
    }
  }

  const fetchAccounts = async (): Promise<void> => {
    if (!currentUser.value || !currentStore.value) return
    if (!accountDirectoryUnsubscribe) {
      let initialSnapshot = true
      const subscriptionEpoch = accountSubscriptionEpoch
      accountDirectoryUnsubscribe = onSnapshot(
        doc(db, `StoresCustomers/${currentStore.value.storeCode}/_AccountDirectory/state`),
        () => {
          if (subscriptionEpoch !== accountSubscriptionEpoch) return
          if (initialSnapshot) {
            initialSnapshot = false
            return
          }
          void refreshAccountDirectory()
        },
        (error) => {
          if (subscriptionEpoch !== accountSubscriptionEpoch) return
          accountDirectoryUnsubscribe = null
          accountsLoadError.value = accountsLoadMessage(error)
          stopLoading('Loading Store Accounts')
        }
      )
    }
    await refreshAccountDirectory()
  }

  const setIncludeInactiveAccounts = async (include: boolean): Promise<void> => {
    if (includeInactiveAccounts.value === include) return
    includeInactiveAccounts.value = include
    if (!include && accountDirectory.value) {
      selectedAccountIds = new Set(accountDirectory.value.activeIds)
      for (const [key, unsubscribe] of accountProfileListeners) {
        if ((JSON.parse(key) as string[]).every((id) => selectedAccountIds.has(id))) continue
        unsubscribe()
        accountProfileListeners.delete(key)
        accountProfileBatches.delete(key)
        pendingAccountBatches.delete(key)
      }
      rebuildCustomers()
    }
    await fetchAccounts()
  }

  // An explicitly selected owner/historical reference can still be read without
  // loading the entire inactive directory. Keep that single record reactive.
  const loadReferencedAccount = async (id: string): Promise<void> => {
    if (customers.value.has(id) || referencedAccountListeners.has(id)) return
    const storeCode = selectedStoreCode.value
    const subscriptionEpoch = accountSubscriptionEpoch
    if (!storeCode) return
    await new Promise<void>((resolve, reject) => {
      const unsubscribe = onSnapshot(
        schoolAccountDoc(id, storeCode),
        (snapshot) => {
          if (
            subscriptionEpoch !== accountSubscriptionEpoch ||
            selectedStoreCode.value !== storeCode
          ) {
            resolve()
            return
          }
          if (!snapshot.exists()) {
            referencedAccounts.delete(id)
            rebuildCustomers()
            resolve()
            return
          }
          let account: Account
          try {
            account = markRaw(snapshot.data())
          } catch (error) {
            reject(error)
            return
          }
          if (!account.hasStore(storeCode)) {
            referencedAccounts.delete(id)
            rebuildCustomers()
            reject(new Error('Account does not belong to this store.'))
            return
          }
          referencedAccounts.set(id, account)
          rebuildCustomers()
          resolve()
        },
        (error) => {
          referencedAccountListeners.delete(id)
          reject(error)
        }
      )
      referencedAccountListeners.set(id, unsubscribe)
    })
  }

  const fetchAircraft = async (): Promise<void> => {
    if (!currentUser.value || !currentStore.value) return

    const storeCode = currentStore.value.storeCode
    const acQuery = query(currentStore.value.aircraftCollection.withConverter(AircraftConverter))

    const unsub = onSnapshot(
      acQuery,
      (querySnapshot) => {
        const newAircraft = new Map<string, Aircraft>()

        querySnapshot.docs.forEach((document) => {
          try {
            const ac = markRaw(new Aircraft(document.data()))
            // Aircraft's own constructor falls back to admin.stores[0], but
            // admin.stores is an array — for a multi-store aircraft that could
            // differ from the store this query actually fetched it from. Set it
            // explicitly to the store we KNOW it came from, same pattern as
            // fetchRooms()/fetchClients() below.
            ac.setStore(storeCode)
            newAircraft.set(ac.info.tailNumber, ac)
          } catch (e) {
            console.error('fetchAircraft: FAILED to convert doc', document.id, e)
          }
        })

        aircraft.value = newAircraft
      },
      (error: FirestoreError) => {
        console.error(`Error fetching aircraft: ${error.code} ${error.message}`)
        aircraft.value = new Map()
      }
    )

    storeUnsubscribes.push(unsub)
  }

  const fetchRooms = async (): Promise<void> => {
    if (!currentUser.value || !currentStore.value) return

    const storeCode = currentStore.value.storeCode
    const roomQuery = query(currentStore.value.roomsCollection.withConverter(RoomConverter))

    const unsub = onSnapshot(
      roomQuery,
      (querySnapshot) => {
        const nextRooms: Room[] = []

        querySnapshot.docs.forEach((document) => {
          try {
            const room = new Room(document.data())
            room.setStore(storeCode)
            nextRooms.push(markRaw(room))
          } catch (e) {
            console.error('fetchRooms: FAILED to convert doc', document.id, e)
          }
        })

        rooms.value = nextRooms
      },
      (error: FirestoreError) => {
        console.error(`Error fetching rooms: ${error.code} ${error.message}`)
        rooms.value = []
      }
    )

    storeUnsubscribes.push(unsub)
  }

  const fetchEvents = async (centerDate: Date = new Date()): Promise<void> => {
    if (
      !currentUser.value ||
      !currentStore.value ||
      !currentStore.value.hasModule(Module.Scheduling)
    ) {
      clearEventSubscription()
      events.value = []
      eventWindow.value = null
      return
    }

    const nextWindow = getThreeMonthEventWindow(centerDate)
    if (isSameEventWindow(eventWindow.value, nextWindow)) return

    clearEventSubscription()
    eventWindow.value = nextWindow
    startLoading('Loading Events')

    const eventQuery = query(
      currentStore.value.eventsCollection.withConverter(ScheduledEventConverter),
      where('eventDetail.startDate', '>=', nextWindow.start),
      where('eventDetail.startDate', '<', nextWindow.end)
    )

    const unsub = onSnapshot(
      eventQuery,
      (querySnapshot) => {
        const nextEvents: ScheduledEvent[] = []

        querySnapshot.docs.forEach((document) => {
          try {
            nextEvents.push(markRaw(new ScheduledEvent(document.data())))
          } catch (e) {
            console.error('fetchEvents: FAILED to convert doc', document.id, e)
          }
        })

        events.value = nextEvents
          .filter((event) => !event.isCancelled)
          .sort((a, b) => a.eventDetail.startDate.getTime() - b.eventDetail.startDate.getTime())
        stopLoading('Loading Events')
      },
      (error: FirestoreError) => {
        console.error(`Error fetching scheduled events: ${error.code} ${error.message}`)
        events.value = []
        eventWindow.value = null
        stopLoading('Loading Events')
      }
    )

    eventUnsubscribe = unsub
    storeUnsubscribes.push(unsub)
  }

  const fetchClients = async (): Promise<void> => {
    if (!currentUser.value || !currentStore.value) return

    const storeCode = currentStore.value.storeCode
    const clientQuery = query(currentStore.value.clientCollection.withConverter(ClientConverter))

    const unsub = onSnapshot(
      clientQuery,
      (querySnapshot) => {
        const newClients = new Map<string, Client>()

        querySnapshot.docs.forEach((document) => {
          if (document.get('lifecycle.status') === 'archived') return
          try {
            const client = markRaw(new Client(document.data()))
            client.setId(storeCode)
            newClients.set(client.firebaseInfo.__id, client)
          } catch (e) {
            console.error('fetchClients: FAILED to convert doc', document.id, e)
          }
        })

        clients.value = newClients
      },
      (error: FirestoreError) => {
        console.error(`Error fetching clients: ${error.code} ${error.message}`)
        clients.value = new Map()
      }
    )

    storeUnsubscribes.push(unsub)
  }

  return {
    currentUser,
    currentStore,
    billingChangeInProgress,
    settingsVersion,
    selectedStore,
    selectedStoreCode,
    pilots,
    instructors,
    customers,
    customersList,
    totalAccountCount,
    schoolStudentCount,
    accountsLoading,
    accountsLoadError,
    includeInactiveAccounts,
    setIncludeInactiveAccounts,
    loadReferencedAccount,
    aircraft,
    aircraftList,
    events,
    eventWindow,
    rooms,

    accountLoadError,
    storeLoadError,

    VALIDATION,

    isAnythingLoading,
    isBlockingLoad,
    isStoreBillingCurrent,

    startLoading,
    stopLoading,
    clearAllLoading,

    fetchAccount,
    fetchAccounts,
    fetchStore,
    fetchEvents,
    userLogout,

    clients,
    clientsList,
    fetchClients
  }
})

function getThreeMonthEventWindow(centerDate: Date): { start: Date; end: Date } {
  const start = new Date(centerDate.getFullYear(), centerDate.getMonth() - 1, 1)
  start.setHours(0, 0, 0, 0)

  const end = new Date(centerDate.getFullYear(), centerDate.getMonth() + 2, 1)
  end.setHours(0, 0, 0, 0)

  return { start, end }
}

function isSameEventWindow(
  currentWindow: { start: Date; end: Date } | null,
  nextWindow: { start: Date; end: Date }
): boolean {
  return (
    !!currentWindow &&
    currentWindow.start.getTime() === nextWindow.start.getTime() &&
    currentWindow.end.getTime() === nextWindow.end.getTime()
  )
}
